import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, Globe, Shield, MessageCircle, QrCode } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import BrandLogo from "./BrandLogo";
import ReferralBox from "./ReferralBox";

// Single concierge phone for the whole footer. WhatsApp URL strips the '+'
// and any spaces — wa.me requires raw country-code + digits.
const CONCIERGE_PHONE = "+1 445 45Jante (52683)";
const CONCIERGE_PHONE_RAW = CONCIERGE_PHONE.replace(/[^\d+]/g, "");
const WHATSAPP_URL = "https://wa.me/14454552683";

export default function Footer() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === "admin";
  const adminHref = isAdmin ? "/admin" : "/login?restricted=admin&next=/admin";
  return (
    <footer
      className="border-t border-[var(--emerald)]/10 mt-24 py-16 px-6 lg:px-12 bg-[var(--ivory-2)]"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-12">
        {/* Left brand block — no subsidiary tagline, no Janté Lux link here */}
        <div className="min-w-0">
          <BrandLogo className="h-24 mb-5" />
          <p className="text-sm leading-relaxed text-[var(--ink-2)] max-w-sm">
            {t("footer.tagline")}
          </p>
        </div>

        {/* Travel pillars — Flights · Cars · Activities · Properties */}
        <div className="min-w-0">
          <div className="overline mb-4">{t("footer.travel")}</div>
          <ul className="text-sm text-[var(--ink-2)] space-y-2.5">
            <li className="whitespace-nowrap">{t("footer.flights")}</li>
            <li className="whitespace-nowrap">{t("footer.cars")}</li>
            <li className="whitespace-nowrap">{t("footer.activities")}</li>
            <li className="whitespace-nowrap">{t("footer.properties")}</li>
          </ul>
        </div>

        {/* Concierge — email · phone · whatsapp · web */}
        <div className="min-w-0">
          <div className="overline mb-4">{t("footer.concierge")}</div>
          <ul className="text-sm text-[var(--ink-2)] space-y-3">
            <li className="flex items-center gap-2.5 whitespace-nowrap">
              <Mail size={14} strokeWidth={1.25} className="text-[var(--brand-red)] shrink-0" />
              <a
                href="mailto:Concierge@JanteTravelConcierges.com"
                className="hover:text-[var(--brand-red)] transition-colors truncate"
                data-testid="footer-email"
              >
                Concierge@JanteTravelConcierges.com
              </a>
            </li>
            <li className="flex items-center gap-2.5 whitespace-nowrap">
              <Phone size={14} strokeWidth={1.25} className="text-[var(--brand-red)] shrink-0" />
              <a
                href={`tel:${CONCIERGE_PHONE_RAW}`}
                className="hover:text-[var(--brand-red)] transition-colors"
                data-testid="footer-phone"
              >
                {CONCIERGE_PHONE}
              </a>
            </li>
            <li className="flex items-center gap-2.5 whitespace-nowrap">
              <MessageCircle size={14} strokeWidth={1.25} className="text-[var(--brand-red)] shrink-0" />
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="hover:text-[var(--brand-red)] transition-colors"
                data-testid="footer-whatsapp"
              >
                WhatsApp the concierge
              </a>
            </li>
            <li className="flex items-center gap-2.5 whitespace-nowrap">
              <Globe size={14} strokeWidth={1.25} className="text-[var(--brand-red)] shrink-0" />
              <a
                href="https://JanteTravelConcierges.com"
                target="_blank"
                rel="noreferrer"
                className="hover:text-[var(--brand-red)] transition-colors"
                data-testid="footer-website"
              >
                JanteTravelConcierges.com
              </a>
            </li>
            <li className="flex items-center gap-2.5 whitespace-nowrap">
              <QrCode size={14} strokeWidth={1.25} className="text-[var(--brand-red)] shrink-0" />
              <Link
                to="/qr"
                className="hover:text-[var(--brand-red)] transition-colors"
                data-testid="footer-qr-link"
              >
                Scan-to-start QR
              </Link>
            </li>
          </ul>
          <ReferralBox />
        </div>
      </div>

      {/* Legal links band — 3-column grid; each label stays on a single line */}
      <div
        className="max-w-7xl mx-auto mt-12 pt-6 border-t border-[var(--emerald)]/10 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.18em] text-[var(--brand-red)]"
        data-testid="footer-legal-strip"
      >
        <Link to="/legal" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-all">{t("footer.allPolicies")}</Link>
        <Link to="/legal#sms" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-sms">{t("footer.smsConsent")}</Link>
        <Link to="/legal#calls" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-calls">{t("footer.recordedCalls")}</Link>
        <Link to="/legal#bookings" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-bookings">{t("footer.bookings")}</Link>
        <Link to="/legal#cancellations" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-cancellations">{t("footer.cancellations")}</Link>
        <Link to="/legal#privacy" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-privacy">{t("footer.privacy")}</Link>
        <Link to="/legal#documents" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-documents">{t("footer.documentControl")}</Link>
        <Link to="/legal#accessibility" className="hover:text-[var(--emerald)] transition-colors sm:whitespace-nowrap" data-testid="footer-legal-accessibility">{t("footer.accessibility")}</Link>
      </div>

      {/* Bottom bar — 3 tight columns, every entry on a single line */}
      <div className="max-w-7xl mx-auto mt-14 pt-6 border-t border-[var(--emerald)]/10 grid sm:grid-cols-3 gap-3 items-center text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] text-[var(--muted)]">
        <div className="text-left sm:whitespace-nowrap">
          © 2023–{new Date().getFullYear()} Janté Luxury Travel Inc.
        </div>
        <div
          className="text-center text-[var(--brand-red)] flex flex-col items-center gap-1 sm:whitespace-nowrap"
          data-testid="footer-center-bottom"
        >
          <span>{t("footer.subsidiary")}</span>
          <a
            href="https://JanteLuxHotels.com"
            target="_blank"
            rel="noreferrer"
            className="text-[var(--emerald)]/80 hover:text-[var(--brand-red)] transition-colors"
            data-testid="footer-sister-brand-bottom"
          >
            Janté Lux Hotels &amp; Residences
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:justify-end sm:flex-nowrap sm:whitespace-nowrap">
          <span className="text-[var(--brand-red)]">Janté Travel Concierges™</span>
          <Link
            to={adminHref}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors tracking-[0.2em] whitespace-nowrap"
            data-testid="footer-admin-link"
          >
            <Shield size={11} strokeWidth={1.5} /> {t("nav.admin")}
          </Link>
        </div>
      </div>
    </footer>
  );
}
